import React, { useEffect, useState, useRef } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { FiImage, FiSearch, FiX, FiLayers, FiArrowRight } from "react-icons/fi";

import ClientCalling from "../../api/ClientCalling";

/* Single chip in the top bar */
const Chip = ({ active, label, count, icon, onClick }) => (
    <motion.button
        whileTap={{ scale: 0.94 }}
        onClick={onClick}
        className='flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] md:text-xs font-semibold transition-colors'
        style={
            active
                ? {
                      color: "#fff",
                      background: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                      boxShadow: "0 8px 20px -8px rgba(127,0,255,0.6)",
                  }
                : {
                      color: "rgba(255,255,255,0.8)",
                      background: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.12)",
                  }
        }
    >
        {icon}
        <span className='whitespace-nowrap'>{label}</span>
        {count != null && (
            <span
                className='tabular-nums text-[9px] md:text-[10px] px-1.5 py-0.5 rounded-full'
                style={{
                    background: active
                        ? "rgba(255,255,255,0.22)"
                        : "rgba(255,255,255,0.08)",
                }}
            >
                {count}
            </span>
        )}
    </motion.button>
);

/* Sticky, horizontally-scrollable category bar with a search toggle + autocomplete.
   activeSlug = "" → "All" is highlighted. initialQuery opens the bar in search mode. */
const WallpaperCatBar = ({ activeSlug = "", initialQuery = "" }) => {
    const navigate = useNavigate();
    const [cats, setCats] = useState([]);
    const [searching, setSearching] = useState(!!initialQuery);
    const [query, setQuery] = useState(initialQuery);
    const [suggest, setSuggest] = useState({ wallpapers: [], categories: [] });
    const [showSuggest, setShowSuggest] = useState(false);
    const inputRef = useRef(null);
    const blurTimer = useRef(null);

    /* Theme the page scrollbar to match the dark wallpaper UI (removed on leave) */
    useEffect(() => {
        const el = document.documentElement;
        el.classList.add("wp-scroll");
        return () => el.classList.remove("wp-scroll");
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const resp = await ClientCalling.getWallpaperCategoryClient(1, 100);
                if (resp?.status && Array.isArray(resp.data)) setCats(resp.data);
            } catch (e) {
                /* ignore */
            }
        })();
    }, []);

    /* keep search box in sync when navigating between search queries */
    useEffect(() => {
        setQuery(initialQuery);
        if (initialQuery) setSearching(true);
    }, [initialQuery]);

    /* focus the input as soon as search mode opens */
    useEffect(() => {
        if (searching) inputRef.current?.focus();
    }, [searching]);

    /* debounced autocomplete — fetch suggestions as the user types */
    useEffect(() => {
        if (!searching) return;
        const q = query.trim();
        if (!q) {
            setSuggest({ wallpapers: [], categories: [] });
            return;
        }
        const t = setTimeout(async () => {
            try {
                const resp = await ClientCalling.getWallpaperSuggestClient(q, 8);
                if (resp?.status) {
                    setSuggest({
                        wallpapers: resp.wallpapers || [],
                        categories: resp.categories || [],
                    });
                    setShowSuggest(true);
                }
            } catch (e) {
                /* ignore */
            }
        }, 250);
        return () => clearTimeout(t);
    }, [query, searching]);

    const submitSearch = () => {
        const q = query.trim();
        setShowSuggest(false);
        if (q) navigate(`/wallpaper/search?q=${encodeURIComponent(q)}`);
    };

    const closeSearch = () => {
        setSearching(false);
        setQuery("");
        setShowSuggest(false);
        setSuggest({ wallpapers: [], categories: [] });
        if (initialQuery) navigate("/wallpaper"); // leaving a search results page
    };

    const goWallpaper = (s) => { setShowSuggest(false); navigate(`/wallpaper/view/${s.w_slug}`); };
    const goCategory = (s) => { setShowSuggest(false); navigate(`/wallpaper/category/${s.w_slug}`); };

    const hasSuggest = suggest.wallpapers.length > 0 || suggest.categories.length > 0;

    return (
        <div className='wp-catbar sticky top-0 z-40'>
            <div className='relative mx-auto max-w-7xl px-4 sm:px-6 md:px-8'>
                <div className='flex items-center gap-2 py-3'>
                    {searching ? (
                        /* ===== search mode — categories hidden ===== */
                        <div className='flex items-center gap-2 w-full'>
                            <button
                                onClick={submitSearch}
                                className='flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                }}
                                aria-label='Search'
                            >
                                <FiSearch className='text-sm' />
                            </button>
                            <input
                                ref={inputRef}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => { if (hasSuggest) setShowSuggest(true); }}
                                onBlur={() => {
                                    blurTimer.current = setTimeout(() => setShowSuggest(false), 150);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") submitSearch();
                                    if (e.key === "Escape") closeSearch();
                                }}
                                placeholder='Search wallpapers…'
                                className='flex-1 min-w-0 bg-transparent text-client-font placeholder:text-client-font/40 text-sm md:text-base focus:outline-none'
                                style={{ caretColor: "#FF8AFF" }}
                            />
                            <button
                                onClick={closeSearch}
                                className='flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-client-font/80'
                                style={{
                                    background: "rgba(255,255,255,0.06)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                }}
                                aria-label='Close search'
                            >
                                <FiX className='text-base' />
                            </button>
                        </div>
                    ) : (
                        /* ===== category mode ===== */
                        <>
                            <button
                                onClick={() => setSearching(true)}
                                className='flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-client-font/85'
                                style={{
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                }}
                                aria-label='Search wallpapers'
                            >
                                <FiSearch className='text-sm' />
                            </button>

                            <div className='flex items-center gap-2 overflow-x-auto no-scrollbar flex-1'>
                                <Chip
                                    active={activeSlug === ""}
                                    icon={<FiImage className='text-xs' />}
                                    label='All'
                                    onClick={() => navigate("/wallpaper")}
                                />
                                {cats.map((c) => (
                                    <Chip
                                        key={c._id}
                                        active={activeSlug === c.w_slug}
                                        label={c.w_name}
                                        count={c.w_count}
                                        onClick={() =>
                                            navigate(`/wallpaper/category/${c.w_slug}`)
                                        }
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* ===== autocomplete dropdown ===== */}
                {searching && showSuggest && query.trim() && hasSuggest && (
                    <div
                        onMouseDown={(e) => e.preventDefault()} // keep input focused on click
                        className='absolute left-3 right-3 sm:left-5 sm:right-5 md:left-7 md:right-7 top-full mt-1 z-50 rounded-2xl overflow-hidden max-h-[70vh] overflow-y-auto'
                        style={{
                            background: "rgba(16,12,22,0.98)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            boxShadow: "0 24px 60px -20px rgba(0,0,0,0.8)",
                        }}
                    >
                        {/* categories */}
                        {suggest.categories.length > 0 && (
                            <div className='py-1.5'>
                                <p className='px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-client-font/40 font-semibold'>
                                    Categories
                                </p>
                                {suggest.categories.map((c) => (
                                    <button
                                        key={`c-${c.w_slug}`}
                                        onClick={() => goCategory(c)}
                                        className='w-full flex items-center gap-3 px-4 py-2 hover:bg-white/5 text-left'
                                    >
                                        <span
                                            className='w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0'
                                            style={{
                                                background:
                                                    "linear-gradient(135deg, rgba(127,0,255,0.28), rgba(252,250,2,0.06))",
                                            }}
                                        >
                                            <FiLayers style={{ color: "#FCFA02" }} className='text-sm' />
                                        </span>
                                        <span className='text-client-font text-sm truncate'>{c.w_name}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* wallpapers */}
                        {suggest.wallpapers.length > 0 && (
                            <div className='py-1.5 border-t border-white/5'>
                                <p className='px-4 pt-1 pb-1 text-[10px] uppercase tracking-wider text-client-font/40 font-semibold'>
                                    Wallpapers
                                </p>
                                {suggest.wallpapers.map((w) => (
                                    <button
                                        key={`w-${w.w_slug}`}
                                        onClick={() => goWallpaper(w)}
                                        className='w-full flex items-center gap-3 px-4 py-2 hover:bg-white/5 text-left'
                                    >
                                        <img
                                            src={w.w_thumbnail}
                                            alt=''
                                            className='w-8 h-12 object-cover rounded-md flex-shrink-0 bg-white/5'
                                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                        />
                                        <span className='text-client-font text-sm truncate'>{w.w_name}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* see all results */}
                        <button
                            onClick={submitSearch}
                            className='w-full flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5 hover:bg-white/5 text-left'
                        >
                            <span className='text-client-title text-sm font-semibold'>
                                See all results for “{query.trim()}”
                            </span>
                            <FiArrowRight className='text-client-title' />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WallpaperCatBar;
